
import { useEffect, useMemo } from 'react'

import { Box, Grid, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  ControlledAutocomplete,
  ControlledDateTimePicker,
  ControlledTextField,
  IAutocompleteOptions,
} from 'components/common/controller-wrapper'
import { Mode, VehicleBodyTypeValues } from 'constants/index'
import {
  CreateAndUpdateDriverBidDtoInput,
  useGetCurrenciesQuery,
  useGetDriverByCurrentUserQuery,
  useGetOwnersVehiclesQuery,
} from 'generated/graphql'

interface SubmitBudFormProps {
  mode: number
}

export const SubmitBidForm = ({ mode }: SubmitBudFormProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CreateAndUpdateDriverBidDtoInput>()

  const selectedVehicleId = useWatch({ control, name: 'vehicleId' })

  const { data: driverData } = useGetDriverByCurrentUserQuery()

  const driverCurrencyId = String(driverData?.driverByCurrentUser?.currencyId)

  const { data: currenciesData, loading: currenciesLoading } = useGetCurrenciesQuery({
    fetchPolicy: 'network-only',
  })

  const { data: vehiclesData, loading: vehiclesLoading } = useGetOwnersVehiclesQuery({
    fetchPolicy: 'network-only',
  })

  const vehicleItems: IAutocompleteOptions = useMemo(() => {
    if (vehiclesData?.ownersVehicles) {
      return vehiclesData.ownersVehicles.map((vehicle) => ({
        label: `${vehicle.vehicleType.name} - ${vehicle.registrationNumber}`,
        value: vehicle.id,
      }))
    }

    return []
  }, [vehiclesData?.ownersVehicles])

  const defaultVehicleId =
    vehiclesData?.ownersVehicles &&
    vehiclesData.ownersVehicles?.find(
      (vehicle) => vehicle.id === driverData?.driverByCurrentUser?.activeVehicleId,
    )?.id

  const currencyItems: IAutocompleteOptions = useMemo(() => {
    if (currenciesData?.currencies) {
      return currenciesData.currencies.map((currency) => ({
        label: currency.code,
        value: currency.id,
      }))
    }

    return []
  }, [currenciesData?.currencies])

  const selectedVehicle = useMemo(() => {
    return vehiclesData?.ownersVehicles?.find((v) => v.id === selectedVehicleId)
  }, [selectedVehicleId, vehiclesData?.ownersVehicles])

  const selectedBodyTypeLabel = useMemo(() => {
    return VehicleBodyTypeValues.find((option) => option.value === selectedVehicle?.bodyType)?.label
  }, [selectedVehicle])

  const selectedSpecialServices = useMemo(() => {
    return selectedVehicle?.vehicleSpecialServices?.map((v) => v.specialService.name) || []
  }, [selectedVehicle])

  const specialServicesValues = selectedSpecialServices.join(', ')

  useEffect(() => {
    if (mode === Mode.Create) {
      if (driverCurrencyId) {
        setValue('currencyId', driverCurrencyId)
      }
      if (defaultVehicleId) {
        setValue('vehicleId', defaultVehicleId)
      }
    }
  }, [mode, driverCurrencyId, setValue, defaultVehicleId])

  const spacing = 3
  return (
    <Grid container spacing={spacing} sx={{ paddingTop: 1 }}>
      <Grid item xs={12} sm={6}>
        <ControlledDateTimePicker
          control={control}
          name='eta'
          label='Estimated time of arrival'
          error={!!errors.eta}
          helperText={errors.eta?.message}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <ControlledTextField
          control={control}
          name='price'
          label='Price'
          required
          error={!!errors.price}
          helperText={errors.price?.message}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <ControlledAutocomplete
          control={control}
          name='vehicleId'
          label='Please select a vehicle'
          options={vehicleItems}
          loading={vehiclesLoading}
          required
          error={!!errors.vehicleId}
          helperText={errors.vehicleId?.message}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <ControlledAutocomplete
          control={control}
          name='currencyId'
          label='Currency'
          options={currencyItems}
          loading={currenciesLoading}
          required
          error={!!errors.currencyId}
          helperText={errors.currencyId?.message}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        {selectedBodyTypeLabel && (
          <Typography variant='body1' sx={{ mb: 0.5 }}>
            <Box component='span' color='primary.main' fontWeight='medium'>
              Body Type:
            </Box>{' '}
            {selectedBodyTypeLabel}
          </Typography>
        )}

        {specialServicesValues && (
          <Typography variant='body1'>
            <Box component='span' color='primary.main' fontWeight='medium'>
              Special Services:
            </Box>{' '}
            {specialServicesValues}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <ControlledTextField
          control={control}
          name='notes'
          label='Any other notes'
          error={!!errors.notes}
          helperText={errors.notes?.message}
        />
      </Grid>
    </Grid>
  )
}
